import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { baseUrl } from '@utils/utils-config'

import Link from 'next/link'
import type React from 'react'
import { Fragment } from 'react'

import { cx } from '../../cx'
import { uiTestId } from '../../test-ids'

export type BreadcrumbItem = {
  label: string
  url: string
  truncate?: boolean
}

export type Props = {
  links: Array<BreadcrumbItem>
  className?: string
  withRoot?: boolean
  placement?: 'top' | 'bottom'
  useFluidContainer?: boolean
}

export function Breadcrumb({
  links,
  withRoot = true,
  className,
  placement = 'top',
  useFluidContainer = true,
}: Props) {
  const root: BreadcrumbItem = {
    label: 'Start',
    url: '/',
  }

  const items = withRoot ? [root, ...links] : links

  return (
    <nav
      className={cx('py-4', className, {
        'fluid-container': useFluidContainer,
        'py-6': placement === 'bottom',
      })}
      aria-label="Breadcrumb"
      data-testid={uiTestId('Breadcrumb.root')}
    >
      <BreadcrumbStructuredData items={items} />
      <BreadcrumbList className={cx({ 'flex-wrap': placement === 'bottom' })}>
        {items.length === 1 ? (
          <BreadcrumbItem>
            <BreadcrumbSeparator>
              <CaretLeftIcon />
            </BreadcrumbSeparator>
            <BreadcrumbLink href={root.url}>{root.label}</BreadcrumbLink>
          </BreadcrumbItem>
        ) : (
          items.map((item, index) => {
            const isPrevious = index === items.length - 2
            const isCurrent = index === items.length - 1

            const title = item.truncate ? item.label : undefined

            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: not a concern for breadcrumbs
              <Fragment key={index}>
                {isPrevious && placement === 'top' && (
                  <BreadcrumbSeparator className="sm:hidden">
                    <CaretLeftIcon />
                  </BreadcrumbSeparator>
                )}
                <BreadcrumbItem
                  className={cx({
                    'truncate': item.truncate,
                    'font-normal text-primary': isCurrent,
                    'hidden sm:inline-flex': !isPrevious && placement === 'top',
                    'font-normal sm:font-semibold': isPrevious,
                  })}
                >
                  {isCurrent && (
                    <BreadcrumbPage
                      className={cx({
                        truncate: item.truncate,
                      })}
                      title={title}
                    >
                      {item.label}
                    </BreadcrumbPage>
                  )}
                  {!isCurrent && (
                    <>
                      <BreadcrumbLink
                        className={cx({
                          truncate: item.truncate,
                        })}
                        href={item.url}
                        title={title}
                      >
                        {item.label}
                      </BreadcrumbLink>

                      <BreadcrumbSeparator
                        className={cx({
                          'hidden sm:block': placement === 'top',
                        })}
                      />
                    </>
                  )}
                </BreadcrumbItem>
              </Fragment>
            )
          })
        )}
      </BreadcrumbList>
    </nav>
  )
}

function BreadcrumbList({ children, className, ...props }: React.ComponentPropsWithoutRef<'ol'>) {
  return (
    <ol
      className={cx(
        'wrap-break-word flex items-center truncate font-base-copy font-semibold leading-none',
        className
      )}
      {...props}
    >
      {children}
    </ol>
  )
}

function BreadcrumbItem({ children, className, ...props }: React.ComponentPropsWithoutRef<'li'>) {
  return (
    <li className={cx('inline-flex items-center', className)} {...props}>
      {children}
    </li>
  )
}

function BreadcrumbLink({ children, ...props }: React.ComponentPropsWithoutRef<typeof Link>) {
  return <Link {...props}>{children}</Link>
}

function BreadcrumbPage({ children, className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <a
      aria-disabled="true"
      aria-current="page"
      className={cx('font-normal text-primary', className)}
      {...props}
    >
      {children}
    </a>
  )
}

function BreadcrumbStructuredData({ items }: { items: Array<BreadcrumbItem> }) {
  const listItems = items
    .filter(item => item.url)
    .map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: baseUrl(item.url),
    }))

  if (listItems.length < 2) return null

  const json = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: listItems,
  }).replace(/</g, '\\u003c')

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span aria-hidden="true" className={cx('shrink-0 px-2 [&>svg]:size-5', className)} {...props}>
      {children ?? <CaretRightIcon aria-hidden="true" />}
    </span>
  )
}
